import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Button,
  TextField,
  Chip,
  Grid,
  Tooltip,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import TrackerPageHeader from "components/TrackerPageHeader";

import {
  useAddCostMutation,
    useGetCostTrackerQuery,
    useLazyGetCostTrackerQuery,
  // recommended if you have mutation:
  // useUpsertWeeklyActualMutation,
} from "store/slices/apis/costSlice";
import ReviewWeeklyActualUpdate from "./ReviewWeeklyActualUpdate";
import ConfirmationDialog from "components/ConfirmationDialog";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "react-redux";
import { useGetScheduleTrackerQuery, useLazyGetScheduleTrackerQuery } from "store/slices/apis/scheduleSlice";

/* ---------------- helpers ---------------- */

const fmt = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "-"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const clampNonNegNumber = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const num = Number(v);
  if (Number.isNaN(num)) return "";
  return Math.max(0, num);
};

function StatChip({ label, value, variant = "filled", color = "default" }) {
  return (
    <Chip
      size="small"
      variant={variant}
      color={color}
      label={
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <span style={{ opacity: 0.8 }}>{label}:</span>
          <strong>{value}</strong>
        </Box>
      }
    />
  );
}

const getRows = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;

  // common wrappers
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.rows)) return res.rows;
  if (Array.isArray(res.result)) return res.result;
  if (Array.isArray(res.items)) return res.items;

  // sometimes: { data: { rows: [...] } }
  if (Array.isArray(res.data?.rows)) return res.data.rows;
  if (Array.isArray(res.data?.items)) return res.data.items;

  return [];
};

/* ---------------- mapping ---------------- */

function toNode(row) {
  return {
    id: String(row.activityId),
    parentTaskId: Number(row.parentTaskId ?? 0),
    label: row.activity ?? "",
    raw: row,
  };
}

function toDetail(row) {
  if (!row) return null;

  return {
    id: String(row.activityId),
    label: row.activity ?? "",
    parentTaskId: Number(row.parentTaskId ?? 0),

    bac: row.bac,
    bcws: row.bcws,
    bcwp: row.bcwp,
    ac: row.ac,
    cv: row.cv,
    balanceCost_wrt_BAC: row.balanceCost_wrt_BAC,
    balanceProjectedCost: row.balanceProjectedCost,
    eac: row.eac,
    costVariance_BAC_EA: row.costVariance_BAC_EA,
    pctVariance_wrt_BAC: row.pctVariance_wrt_BAC,

    baselineStart: row.baselineStart,
    baselineFinish: row.baselineFinish,
    actualStart: row.actualStart,
    actualFinish: row.actualFinish,
    anticipatedStart: row.anticipatedStart,
    anticipatedFinish: row.anticipatedFinish,

    
    
 

    projectedSum: row.projectedSum,
    
  };
}

/* ---------------- component ---------------- */

export default function WeeklyActualUpdate() {
  // Header
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [statusDate, setStatusDate] = React.useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [mode, setMode] = React.useState("draft");

  // Left panel (tree)
  const [treeSearch, setTreeSearch] = React.useState("");
  const [expanded, setExpanded] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null);

  // Local caches for loaded tree
  const [rootIds, setRootIds] = React.useState([]);
  const [nodesById, setNodesById] = React.useState(() => new Map()); // id -> node
  const [childrenByParent, setChildrenByParent] = React.useState(
    () => new Map()
  ); // parentId -> childIds[]
  const [childrenCount, setChildrenCount] = React.useState(() => new Map()); // parentId -> count
  const fetchedChildren = React.useRef(new Set()); // parentIds that we've fetched
  const [loadingChildren, setLoadingChildren] = React.useState(() => new Set());

  // Right panel (details + weekly input)
  const [detail, setDetail] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [weeklyCost, setWeeklyCost] = React.useState("");
  const [isUpdateExplicitly, setIsUpdateExplicitly] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [isReview, setIsReview] = useState(false);
  const [isReset, setIsReset] = useState(false);

  // If you have mutation:
  // const [upsertWeeklyActual, { isLoading: saving }] = useUpsertWeeklyActualMutation();
  //const saving = true;

  const [addCostFn, { isLoading: costLoader }] = useAddCostMutation();

  // Root call: since API uses parentId, root is parentId=0
  const rootArgs = React.useMemo(
    () => ({ projectId, statusDate, parentId: 0 }),
    [projectId, statusDate]
  );

  const {
    data: rootData,
    isFetching: loadingRoot,
    isError: rootError,
  } = useGetCostTrackerQuery(rootArgs);

  const [trigger] = useLazyGetCostTrackerQuery();

  const isChildLoading = (id) => loadingChildren.has(id);
  const setChildLoading = (id, val) => {
    setLoadingChildren((prev) => {
      const next = new Set(prev);
      if (val) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // 1) Load root nodes into cache
  React.useEffect(() => {
    if (!rootData) return;

    // reset caches for new week/project
    fetchedChildren.current = new Set();
    setExpanded([]);
    setSelectedId(null);
    setDetail(null);
    setWeeklyCost("");

    const rootRows = getRows(rootData);
    const roots = rootRows.map(toNode);

    setNodesById(() => {
      const m = new Map();
      roots.forEach((n) => m.set(n.id, n));
      return m;
    });

    setChildrenByParent(() => new Map());
    setChildrenCount(() => new Map());
    setRootIds(roots.map((n) => n.id));

    if (roots[0]?.id) setSelectedId(roots[0].id);
  }, [rootData, projectId, statusDate]);

  // 2) Expand -> fetch children (parentId = nodeId)
  const loadChildrenIfNeeded = React.useCallback(
    async (parentId) => {
      if (!parentId) return;

      if (fetchedChildren.current.has(parentId)) return;
      fetchedChildren.current.add(parentId);

      setChildLoading(parentId, true);
      try {
        const res = await trigger(
          { projectId, statusDate, parentId },
          true
        ).unwrap();
        const rows = getRows(res);
        const children = rows.map(toNode);

        setNodesById((prev) => {
          const next = new Map(prev);
          children.forEach((c) => next.set(c.id, c));
          return next;
        });

        setChildrenByParent((prev) => {
          const next = new Map(prev);
          next.set(
            parentId,
            children.map((c) => c.id)
          );
          return next;
        });

        setChildrenCount((prev) => {
          const next = new Map(prev);
          next.set(parentId, children.length);
          return next;
        });
      } finally {
        setChildLoading(parentId, false);
      }
    },
    [trigger, projectId, statusDate]
  );

  const handleExpandedChange = async (_, newExpandedIds) => {
    const newlyExpanded = newExpandedIds.filter((id) => !expanded.includes(id));
    setExpanded(newExpandedIds);

    for (const id of newlyExpanded) {
      await loadChildrenIfNeeded(id);
    }
  };

  // 3) Select -> show details.
  // Since you said: "selected activityId should be sent as parentId to the API"
  // We do that to fetch children, and ALSO use the cached node row as detail.
  // (Optional: if you have a dedicated detail endpoint, we can swap it in.)
  React.useEffect(() => {
    let cancelled = false;

    const loadDetail = async () => {
      if (!selectedId) return;

      // Use cached node as immediate detail (instant UI)
      const cached = nodesById.get(selectedId)?.raw;
      setDetail(toDetail(cached));

      // Also fetch children in background IF you want leaf detection quickly when clicking:
      // (This is optional — comment out if you only want fetch on expand)
      // If you prefer: ONLY expand fetches children, then remove this block.
      setDetailLoading(true);
      try {
        await loadChildrenIfNeeded(selectedId);
        if (cancelled) return;

        // After children load, we can mark leaf/non-leaf based on childrenCount
        // (detail itself remains same)
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId, nodesById, loadChildrenIfNeeded]);

  // leaf inference:
  // - unknown (not fetched) => treat as "maybe leaf"
  // - fetched and childrenCount=0 => leaf
  const inferredIsLeaf = React.useMemo(() => {
    if (!selectedId) return false;
    if (!fetchedChildren.current.has(selectedId)) return false; // unknown; keep edit disabled until proven leaf (safer)
    return (childrenCount.get(selectedId) ?? 0) === 0;
  }, [selectedId, childrenCount]);

  const handleSave = async () => {
    console.log("clicked clale");
    // since no isLeaf, we only allow save when inferred leaf == true
    // if (!detail || !inferredIsLeaf) return;

    const val = clampNonNegNumber(weeklyCost);
    console.log("clicked val", val, weeklyCost);
    if (val === "") return;
    updatePendingUpdates(
      selectedId,
      clampNonNegNumber(val),
      isUpdateExplicitly
    );
    handleReset();
    // Recommended: call your mutation here
    // await upsertWeeklyActual({ projectId, statusDate, activityId: selectedId, weeklyCost: Number(val) }).unwrap();

    //setMode("draft");
  };

  const handleReset = () => {
    setWeeklyCost("");
    setIsUpdateExplicitly(false);
  };

  const handleSubmit = async () => {

    console.log("pending updates", pendingUpdates);
    if (Object.keys(pendingUpdates).length === 0) return;

    try {
      const dataArr = [];
      const statusDateIso = new Date(statusDate).toISOString();
      Object.entries(pendingUpdates).map(([key, value]) => {
        const obj = {
          projectId,
          statusDate: statusDateIso
        };
        obj.activityId = key;
        obj.actualCost = value.value
        obj.isExplicit = value.explicit
        dataArr.push(obj)
      })
      console.log("dataArr", dataArr)
      const payload = { data: dataArr }
      const result = await addCostFn(payload).unwrap();
      openNotification("Cost Updated successfully!", "success");
      setPendingUpdates({});
      setIsReview(false);
    } catch (error) {
      console.error("Submission failed:", error);
      openNotification(error?.data?.message || "Error in Cost Update");
    }

  };

  const openNotification = (msg, type = "error", duration = 5000) => {
    dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: 'alert',
        alert: {
          color: type
        },
        close: false
      })
    );
    setTimeout(() => dispatch(closeSnackbar()), duration);
  };


  const handleReview = () => {
    setIsReview(true);
  };

  const matchesSearch = (node) => {
    if (!treeSearch.trim()) return true;
    return (node?.label || "")
      .toLowerCase()
      .includes(treeSearch.trim().toLowerCase());
  };

  const handleExplicitChange = (value) => {
    if (value === false && weeklyCost) {
      handleReset();
    }
    setIsUpdateExplicitly(value);
  };

  const updatePendingUpdates = (selectedId, value, explicit) => {
    const node = nodesById.get(selectedId);
    console.log("clicked", selectedId, value, explicit, node);
    setPendingUpdates((prev) => ({
      ...prev,
      [selectedId]: { value, explicit, label: node?.label },
    }));
  };

  const handleWeeklyValuesReset = () => {
    setPendingUpdates({});
    setIsReset(false)
  };

  useEffect(() => {
    if (selectedId) {
      const existing = pendingUpdates[selectedId];
      setWeeklyCost(existing?.value || "");
      setIsUpdateExplicitly(existing?.explicit || false);
    }
  }, [selectedId, pendingUpdates]);

  console.log("pendingUpdates", pendingUpdates);

  const renderNode = (nodeId) => {
    const node = nodesById.get(nodeId);
    if (!node) return null;

    const childIds = childrenByParent.get(nodeId) || [];
    const match = matchesSearch(node);

    // Leaf inference for UI icon:
    const fetched = fetchedChildren.current.has(nodeId);
    const isLeaf = fetched && (childrenCount.get(nodeId) ?? 0) === 0;

    const pending = pendingUpdates[nodeId];
    const isEdited = pending !== undefined;

    return (
      <TreeItem
        key={nodeId}
        itemId={nodeId}
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              opacity: match ? 1 : 0.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 650 }}>
              {node.label}
            </Typography>

            {isLeaf ? (
              <Chip size="small" variant="outlined" label="Leaf" />
            ) : null}

            {isChildLoading(nodeId) ? <CircularProgress size={14} /> : null}
            {isEdited && (
              <Tooltip
                title={`New Actual Cost: ₹${pending.value} ${pending.explicit ? "(Explicitly)" : ""
                  }`}
              >
                <ModeEditOutlineIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            )}
          </Box>
        }
      >
        {childIds.map((cid) => renderNode(cid))}

        {isChildLoading(nodeId) ? (
          <TreeItem
            itemId={`${nodeId}__loading`}
            label={
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ py: 0.5 }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Loading…
                </Typography>
              </Stack>
            }
          />
        ) : null}
      </TreeItem>
    );
  };

  return (
    <>
      <TrackerPageHeader
        title="Cost Update"
        description="Select a WBS activity, enter weekly actual costs, and publish the update."
      />
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Card>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ md: "center" }}
                >
                  <Box sx={{ flex: 1 }}>
                    {/* <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      color={mode === "published" ? "success" : "default"}
                      label={mode === "published" ? "PUBLISHED" : "DRAFT"}
                    />
                    <Tooltip title="Expand WBS on the left. Select an activity to view details on the right. Weekly entry will be enabled for leaf activities.">
                      <InfoOutlinedIcon fontSize="small" sx={{ opacity: 0.7 }} />
                    </Tooltip>
                  </Stack> */}
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                  >
                    {/* <TextField
                    size="small"
                    label="Project"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    sx={{ minWidth: 160 }}
                  /> */}
                    <TextField
                      size="small"
                      label="Status Week Ending"
                      type="date"
                      value={statusDate}
                      onChange={(e) => setStatusDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 210 }}
                    />
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<RestartAltIcon />}
                      onClick={() => setIsReset(true)}
                      disabled={Object.keys(pendingUpdates).length === 0}
                    >
                      Reset
                    </Button>
                    {/* <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!inferredIsLeaf || weeklyCost === "" || saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button> */}
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PublishIcon />}
                      onClick={handleReview}
                      disabled={Object.keys(pendingUpdates).length === 0}
                    >
                      Review and Save
                    </Button>
                  </Stack>
                </Stack>

                <Divider />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {/* <StatChip label="Project" value={projectId} variant="outlined" /> */}
                  <StatChip
                    label="Week"
                    value={statusDate}
                    variant="outlined"
                  />
                  <StatChip label="Selected" value={selectedId || "-"} />
                </Stack>

                {rootError ? (
                  <Typography color="error" variant="body2">
                    Failed to load root hierarchy.
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          {/* Panels */}
          <Grid container spacing={2}>
            {/* Left */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: "calc(100vh - 250px)", minHeight: 560 }}>
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      WBS Hierarchy
                    </Typography>
                    {loadingRoot ? <CircularProgress size={16} /> : null}
                  </Stack>

                  <TextField
                    size="small"
                    placeholder="Search in loaded nodes"
                    value={treeSearch}
                    onChange={(e) => setTreeSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />

                  <Divider sx={{ mb: 1 }} />

                  <Box sx={{ flex: 1, overflow: "auto", pr: 1 }}>
                    <SimpleTreeView
                      expandedItems={expanded}
                      selectedItems={selectedId}
                      onExpandedItemsChange={handleExpandedChange}
                      onSelectedItemsChange={(_, id) => {
                        if (id) {
                          setSelectedId(id);
                          //handleReset();
                        }
                      }}
                      sx={{ "& .MuiTreeItem-content": { py: 0.5 } }}
                    >
                      {rootIds.map((rid) => renderNode(rid))}
                    </SimpleTreeView>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Card sx={{ height: "calc(100vh - 250px)", minHeight: 560 }}>
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Selected Node Details
                    </Typography>
                    {detailLoading ? <CircularProgress size={16} /> : null}
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  {!detail ? (
                    <Box sx={{ p: 2 }}>
                      <Typography color="text.secondary">
                        Select a node from the left to view details and enter
                        weekly cost.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2} sx={{ flex: 1 }}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            {detail.label}
                          </Typography>

                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            sx={{ gap: 1 }}
                          >
                            <StatChip
                              label="Entry Enabled"
                              value={
                                inferredIsLeaf
                                  ? "Yes (Leaf)"
                                  : "No (Parent / Unknown)"
                              }
                              color={inferredIsLeaf ? "success" : "default"}
                            />
                            <StatChip
                              label="BAC"
                              value={fmt(detail.bac)}
                            />
                            <StatChip
                              label="BCWS"
                              value={fmt(detail.bcws)}
                            />
                            <StatChip
                              label="BCWP"
                              value={fmt(detail.bcwp)}
                            />
                            <StatChip
                              label="AC"
                              value={fmt(detail.ac)}
                            />
                            <StatChip
                              label="CV"
                              value={fmt(detail.cv)}
                              variant="outlined"
                            />
                            <StatChip
                              label="Balance cost w.r.t BAC"
                              value={fmt(detail.balanceCost_wrt_BAC)}
                              variant="outlined"
                            />
                            <StatChip
                              label="Balance Projected cost estimated"
                              value={fmt(detail.balanceProjectedCost)}
                              variant="outlined"
                            />
                            <StatChip
                              label="EAC"
                              value={fmt(detail.eac)}
                            />
                            <StatChip
                              label="Cost Variance"
                              value={fmt(detail.costVariance_BAC_EA)}
                            />
                            <StatChip
                              label="Percentage variance w.r.t. BAC"
                              value={fmt(detail.pctVariance_wrt_BAC)}
                            />
                          </Stack>
                        </CardContent>
                      </Card>

                      <Card variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Typography sx={{ fontWeight: 800 }}>
                              Weekly Cost Update
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Weekly entry is enabled only when we confirm the
                              selected node is a <b>leaf</b>
                              (i.e., System found no children).
                            </Typography>

                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ sm: "center" }}
                            >
                              <TextField
                                size="small"
                                label="Actual Cost (This Week)"
                                type="number"
                                inputProps={{ step: "any" }} 
                                value={weeklyCost || ""}
                                onChange={(e) =>
                                  setWeeklyCost(
                                    clampNonNegNumber(e.target.value)
                                  )
                                }
                                disabled={
                                  !inferredIsLeaf && !isUpdateExplicitly
                                }
                                sx={{ minWidth: 280 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₹
                                    </InputAdornment>
                                  ),
                                }}
                              />

                              <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={
                                  (!inferredIsLeaf && !isUpdateExplicitly) ||
                                  weeklyCost === ""
                                }
                              >
                                Save Weekly Cost
                              </Button>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      pendingUpdates[selectedId]?.explicit ||
                                      isUpdateExplicitly
                                    }
                                    onChange={(e) =>
                                      handleExplicitChange(e.target.checked)
                                    }
                                    color="warning"
                                  />
                                }
                                label={
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    I want to update this activity explicitly
                                  </Typography>
                                }
                              />
                            </Stack>

                            {!inferredIsLeaf ? (
                              <Chip
                                color="warning"
                                label="Select a leaf activity to enter weekly cost (or expand this node to verify children)."
                                sx={{ mt: 1 }}
                              />
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Box>
      <ReviewWeeklyActualUpdate
        open={isReview}
        handleClose={() => setIsReview(false)}
        pendingUpdates={pendingUpdates}
        handleSubmit={handleSubmit}
        isLoading={costLoader}
      />
      <ConfirmationDialog open={isReset}
        handleClose={() => setIsReset(false)}
        handleConfirm={handleWeeklyValuesReset}
        title={"Discard changes?"}
        description={"This action will clear all information you’ve entered. This cannot be undone."}
      />
    </>
  );
}
